import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, Check, Sparkles } from 'lucide-react';
import { cn } from '../lib/utils';

function formatTierPrice(cents, currency) {
  const cur = (currency || 'nok').toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format((cents || 0) / 100);
  } catch {
    return `${(cents || 0) / 100} ${cur}`;
  }
}

function serviceSignature(s) {
  if (s.kind === 'catalog') return `c:${s.serviceKey}`;
  return `u:${s.id}`;
}

function tierServiceSignatureSet(services) {
  const set = new Set();
  for (const s of services || []) set.add(serviceSignature(s));
  return set;
}

function isServiceSetSuperset(currentSet, prevSet) {
  for (const x of prevSet) {
    if (!currentSet.has(x)) return false;
  }
  return true;
}

/** Same direction (top → bottom) for every plan; opacity steps up left → right for higher tiers. */
const planSurface = [
  'border-border/70 bg-gradient-to-b from-muted/[0.06] via-card to-primary/[0.02] shadow-sm',
  'border-primary/25 bg-gradient-to-b from-muted/[0.1] via-card to-primary/[0.045] shadow-sm',
  'border-primary/35 bg-gradient-to-b from-muted/[0.14] via-card to-primary/[0.075] shadow-md shadow-primary/[0.05]',
];

const CoachDetailPage = () => {
  const { id } = useParams();
  const { t } = useLanguage();
  const reduceMotion = useReducedMotion();
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutKey, setCheckoutKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id) return;
      try {
        const res = await api.get(`/api/coaches/${id}`);
        if (!cancelled) setCoach(res.data?.coach || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.error || t('coaches.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const startCheckout = async (billingInterval, coachingTierId) => {
    const key = `${billingInterval}-${coachingTierId}`;
    setCheckoutKey(key);
    setError(null);
    try {
      const body = {
        coachUserId: parseInt(id, 10),
        billingInterval,
      };
      const tiers = coach?.tiers || [];
      if (tiers.length > 1) {
        body.coachingTierId = coachingTierId;
      } else if (tiers.length === 1) {
        body.coachingTierId = tiers[0].id;
      }
      const res = await api.post('/api/stripe/coaching/checkout', body);
      if (res.data?.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      setError(e.response?.data?.error || t('coaches.checkoutError'));
    } finally {
      setCheckoutKey(null);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center min-h-[240px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  if (error && !coach) {
    return (
      <div className='max-w-xl mx-auto p-6 space-y-4'>
        <p className='text-sm text-destructive'>{error}</p>
        <Button variant='outline' asChild>
          <Link to='/coaches'>
            <ArrowLeft className='h-4 w-4 mr-2' />
            {t('coaches.backToCatalog')}
          </Link>
        </Button>
      </div>
    );
  }

  const tiers = coach?.tiers || [];

  return (
    <div className='max-w-6xl mx-auto p-6 space-y-8'>
      <Button variant='ghost' size='sm' asChild className='-ml-2'>
        <Link to='/coaches'>
          <ArrowLeft className='h-4 w-4 mr-2' />
          {t('coaches.backToCatalog')}
        </Link>
      </Button>

      <Card className='overflow-hidden border-border/80 shadow-lg'>
        <CardHeader className='flex flex-row items-start gap-4 space-y-0 pb-6 border-b border-border/60 bg-gradient-to-r from-muted/40 via-card to-primary/[0.04]'>
          <Avatar className='h-24 w-24 shrink-0 ring-2 ring-primary/20'>
            {coach?.avatarUrl ? <AvatarImage src={coach.avatarUrl} alt='' /> : null}
            <AvatarFallback className='bg-primary text-primary-foreground text-xl font-semibold tracking-tight'>
              {coach?.name?.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className='min-w-0 pt-1'>
            <CardTitle className='text-3xl sm:text-4xl font-semibold tracking-tight text-foreground text-balance'>
              {coach?.name}
            </CardTitle>
            {coach?.headline && (
              <p className='text-base text-muted-foreground mt-2 font-medium leading-snug max-w-2xl'>
                {coach.headline}
              </p>
            )}
          </div>
        </CardHeader>

        <CardContent className='space-y-10 pt-8'>
          {coach?.bio && (
            <motion.div
              className='rounded-xl border border-border/70 bg-muted/20 px-5 py-5'
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={reduceMotion ? false : { opacity: 1, y: 0 }}
              transition={{ delay: reduceMotion ? 0 : 0.05, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className='text-sm font-semibold text-foreground mb-3 tracking-tight'>{t('coaches.bio')}</h2>
              <p className='text-sm text-foreground leading-relaxed whitespace-pre-wrap'>{coach.bio}</p>
            </motion.div>
          )}

          {tiers.length > 0 && (
            <motion.section
              initial={reduceMotion ? false : { opacity: 0, y: 14 }}
              animate={reduceMotion ? false : { opacity: 1, y: 0 }}
              transition={{
                duration: 0.4,
                delay: reduceMotion ? 0 : 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <div className='flex items-center gap-2 mb-5'>
                <Sparkles className='h-5 w-5 text-primary shrink-0' aria-hidden />
                <div>
                  <h2 className='text-lg font-semibold tracking-tight text-foreground'>{t('coaches.plansRowTitle')}</h2>
                  <p className='text-sm text-muted-foreground'>{t('coaches.planCardTagline')}</p>
                </div>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start'>
                {tiers.map((tier, i) => {
                  const prevTier = i > 0 ? tiers[i - 1] : null;
                  const prevSigSet = prevTier ? tierServiceSignatureSet(prevTier.services) : new Set();
                  const currSigSet = tierServiceSignatureSet(tier.services);
                  const showInheritSummary =
                    i > 0 && prevSigSet.size > 0 && isServiceSetSuperset(currSigSet, prevSigSet);
                  const allServices = tier.services || [];
                  const servicesToList = showInheritSummary
                    ? allServices.filter((s) => !prevSigSet.has(serviceSignature(s)))
                    : allServices;
                  const hasAnyServices = allServices.length > 0;

                  return (
                  <motion.div
                    key={tier.id}
                    className='min-w-0 h-full'
                    initial={reduceMotion ? false : { opacity: 0, y: 20 }}
                    animate={reduceMotion ? false : { opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: reduceMotion ? 0 : 0.16 + i * 0.07,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                  >
                    <div
                      className={cn(
                        'rounded-2xl border p-5 h-full flex flex-col',
                        planSurface[i % planSurface.length]
                      )}
                    >
                      <div className='mb-4'>
                        <h3 className='text-xl font-semibold tracking-tight text-foreground text-balance'>
                          {tier.name}
                        </h3>
                        <p className='text-sm text-muted-foreground mt-2 font-medium tabular-nums'>
                          {formatTierPrice(tier.amountCentsMonth, tier.currency)}
                          <span className='text-muted-foreground/80 font-normal'>{t('coaches.perMonth')}</span>
                          <span className='mx-1.5 text-border'>·</span>
                          {formatTierPrice(tier.amountCentsYear, tier.currency)}
                          <span className='text-muted-foreground/80 font-normal'>{t('coaches.perYear')}</span>
                        </p>
                      </div>

                      <div className='flex-1 min-h-0 border-t border-border/50 pt-4'>
                        {!hasAnyServices ? (
                          <p className='text-xs text-muted-foreground'>{t('coaches.noServicesInTier')}</p>
                        ) : (
                          <div className='space-y-3.5'>
                            {showInheritSummary && (
                              <p className='text-sm font-semibold text-foreground leading-snug tracking-tight'>
                                {t('coaches.allServicesFromPlanPlus', { planName: prevTier.name })}
                              </p>
                            )}
                            {servicesToList.length > 0 ? (
                              <ul className='space-y-3.5'>
                                {servicesToList.map((s) => (
                                  <li
                                    key={
                                      s.kind === 'catalog'
                                        ? `c-${s.serviceKey}-${tier.id}`
                                        : `u-${s.id}-${tier.id}`
                                    }
                                    className='flex gap-3 text-sm'
                                  >
                                    <Check
                                      className='h-4 w-4 shrink-0 text-primary mt-0.5'
                                      strokeWidth={2.5}
                                      aria-hidden
                                    />
                                    <span className='min-w-0'>
                                      {s.kind === 'catalog' ? (
                                        <>
                                          <span className='font-semibold text-foreground block leading-snug'>
                                            {t(`coaches.serviceCatalog.${s.serviceKey}.name`)}
                                          </span>
                                          <span className='block text-xs text-muted-foreground mt-1 leading-relaxed'>
                                            {t(`coaches.serviceCatalog.${s.serviceKey}.description`)}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className='font-semibold text-foreground block leading-snug'>
                                            {s.title}
                                          </span>
                                          {!!s.description && (
                                            <span className='block text-xs text-muted-foreground mt-1 leading-relaxed'>
                                              {s.description}
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </div>
                        )}
                      </div>

                      <div className='mt-5 pt-4 border-t border-border/50'>
                        {!tier.subscribable ? (
                          <p className='text-xs text-muted-foreground'>{t('coaches.tierNotSubscribable')}</p>
                        ) : (
                          <div className='flex flex-col sm:flex-row gap-2'>
                            <Button
                              className='flex-1 font-semibold'
                              disabled={!!checkoutKey}
                              onClick={() => startCheckout('month', tier.id)}
                            >
                              {checkoutKey === `month-${tier.id}` ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                t('coaches.subscribeMonthly')
                              )}
                            </Button>
                            <Button
                              variant='secondary'
                              className='flex-1 font-medium border border-border/80 bg-secondary/80'
                              disabled={!!checkoutKey}
                              onClick={() => startCheckout('year', tier.id)}
                            >
                              {checkoutKey === `year-${tier.id}` ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                              ) : (
                                t('coaches.subscribeYearly')
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                  );
                })}
              </div>
            </motion.section>
          )}

          {error && (
            <p className='text-sm text-destructive' role='alert'>
              {error}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachDetailPage;
